import React, { useEffect, useState } from "react";
import axiosInstance from "../../services/auth";
import ReactPaginate from "react-paginate";
import { toast, ToastContainer } from "react-toastify";
import { useCombinedContext } from "../../context/CombinedContext";
import * as XLSX from "xlsx";
import { Link } from "react-router-dom";

const StockReport = () => {
  const [reportType, setReportType] = useState("date"); // date | month | year | item
  const [reportData, setReportData] = useState([]);
  const [filterData, setFilterData] = useState([]);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");

  const [selectedStockId, setSelectedStockId] = useState("");
  const [stockOptions, setStockOptions] = useState([]);

  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const authData =
    JSON.parse(localStorage.getItem("authData")) ||
    JSON.parse(sessionStorage.getItem("authData"));

  const userRole = authData?.role;
  const { selectedCompany } = useCombinedContext();
  const companyId = selectedCompany?._id;

  const isItemWise = reportType === "item";

  const formatNumber = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return "0.00";
    return num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const fetchStockOptions = async () => {
    try {
      if (!companyId) return;
      const response = await axiosInstance.get(`/logistics/stock/${companyId}`);
      setStockOptions(response?.data?.stocks || []);
    } catch (error) {
      toast.error("Error loading stock options");
    }
  };

  const fetchStockReport = async () => {
    try {
      if (!companyId) return;

      if (reportType === "date" && !fromDate && !toDate) {
        setReportData([]);
        setFilterData([]);
        return;
      }

      if (reportType === "month" && !month) {
        setReportData([]);
        setFilterData([]);
        return;
      }

      if (reportType === "year" && !year) {
        setReportData([]);
        setFilterData([]);
        return;
      }

      if (reportType === "item" && !selectedStockId) {
        setReportData([]);
        setFilterData([]);
        return;
      }

      setLoading(true);

      let response;
      const params = {};

      if (reportType === "date") {
        if (fromDate) params.start_date = fromDate;
        if (toDate) params.end_date = toDate;

        response = await axiosInstance.get(
          `/logistics/stock/report/date-wise/${companyId}`,
          { params },
        );
      }

      if (reportType === "month") {
        params.month = month;

        response = await axiosInstance.get(
          `/logistics/stock/report/month-wise/${companyId}`,
          { params },
        );
      }

      if (reportType === "year") {
        params.year = year;

        response = await axiosInstance.get(
          `/logistics/stock/report/year-wise/${companyId}`,
          { params },
        );
      }

      if (reportType === "item") {
        if (fromDate) params.start_date = fromDate;
        if (toDate) params.end_date = toDate;
        params.stockId = selectedStockId;

        response = await axiosInstance.get(
          `/logistics/stock/report/item-wise/${companyId}`,
          { params },
        );
      }

      const data = response?.data?.data || [];
      const sortedData = [...data].sort((a, b) => {
        if (reportType === "item") {
          return String(a.date).localeCompare(String(b.date));
        }
        return (a.stockNo || 0) - (b.stockNo || 0);
      });

      setReportData(sortedData);

      if (searchText) {
        const filtered = sortedData.filter((item) => {
          const searchable = [
            item.date,
            item.name,
            item.category,
            item.stockNo,
            item.unit,
            item.remark,
          ]
            .map((v) => String(v || "").toLowerCase())
            .join(" ");

          return searchable.includes(searchText);
        });

        setFilterData(filtered);
      } else {
        setFilterData(sortedData);
      }
    } catch (error) {
      toast.error("Error fetching stock report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId && reportType === "item") {
      fetchStockOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, reportType]);

  useEffect(() => {
    if (companyId) {
      fetchStockReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, reportType, fromDate, toDate, month, year, selectedStockId]);

  const handleSearchChange = (e) => {
    const text = e.target.value.toLowerCase();
    setSearchText(text);

    const filtered = reportData.filter((item) => {
      const searchable = [
        item.date,
        item.name,
        item.category,
        item.stockNo,
        item.unit,
        item.remark,
      ]
        .map((v) => String(v || "").toLowerCase())
        .join(" ");

      return searchable.includes(text);
    });

    setFilterData(filtered);
    setCurrentPage(0);
  };

  const handlePageClick = (selectedPage) => {
    setCurrentPage(selectedPage.selected);
  };

  const currentItems = [...filterData].slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage,
  );

  const pageCount = Math.ceil(filterData.length / itemsPerPage);
  const tableColSpan = isItemWise ? 19 : 18;

  const handleExport = () => {
    if (!filterData || filterData.length === 0) {
      toast.info("No data to export");
      return;
    }

    const sheetData = [];
    const merges = [];
    const currentDate = new Date();
    const reportDate = currentDate.toLocaleDateString();

    sheetData.push([`Report Generated Date: ${reportDate}`]);
    merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: isItemWise ? 12 : 11 } });

    sheetData.push([
      `Report Type: ${reportType === "month"
        ? "Month Wise"
        : reportType === "year"
          ? "Year Wise"
          : reportType === "item"
            ? "Item Wise"
            : "Date Wise"
      }`,
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
    ]);
    merges.push({ s: { r: 1, c: 0 }, e: { r: 1, c: isItemWise ? 12 : 11 } });

    sheetData.push([
      `From Date: ${fromDate || "-"}`,
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
    ]);
    merges.push({ s: { r: 2, c: 0 }, e: { r: 2, c: isItemWise ? 12 : 11 } });

    sheetData.push([
      `To Date: ${toDate || "-"}`,
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
    ]);
    merges.push({ s: { r: 3, c: 0 }, e: { r: 3, c: isItemWise ? 12 : 11 } });

    if (isItemWise) {
      const selectedStock = stockOptions.find((x) => x._id === selectedStockId);
      sheetData.push([
        `Selected Item: ${selectedStock ? `${selectedStock.stockNo} - ${selectedStock.name}` : "-"}`,
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
      ]);
      merges.push({ s: { r: 4, c: 0 }, e: { r: 4, c: 12 } });
    } else {
      sheetData.push([
        `Month: ${month || "-"}`,
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
      ]);
      merges.push({ s: { r: 4, c: 0 }, e: { r: 4, c: isItemWise ? 12 : 11 } });

      sheetData.push([
        `Year: ${year || "-"}`,
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
      ]);
      merges.push({ s: { r: 5, c: 0 }, e: { r: 5, c: isItemWise ? 12 : 11 } });
    }

    if (isItemWise) {
      sheetData.push([
        "Sl No",
        "Date",
        "Part No",
        "Product Name",
        "Category",
        "Unit",
        "Opening Qty",
        "Purchased Qty",
        "Used Qty",
        "Closing Qty",
        "Opening A",
        "Purchased A",
        "Transferred A",
        "Closing A",
        "Opening B",
        "Transferred B",
        "Used B",
        "Closing B",
        "Remark",
      ]);

      filterData.forEach((item, index) => {
        sheetData.push([
          index + 1,
          item.date || "",
          item.stockNo || "",
          item.name || "",
          item.category || "",
          item.unit || "",
          item.openingQty ?? 0,
          item.purchasedQty ?? 0,
          item.usedQty ?? 0,
          item.closingQty ?? 0,
          item.openingQtyA ?? 0,
          item.purchasedQtyA ?? 0,
          item.transferredQtyA ?? 0,
          item.closingQtyA ?? 0,
          item.openingQtyB ?? 0,
          item.transferredQtyB ?? 0,
          item.usedQtyB ?? 0,
          item.closingQtyB ?? 0,
          item.remark || "",
        ]);
      });
    } else {
      sheetData.push([
        "Sl No",
        "Part No",
        "Product Name",
        "Category",
        "Unit",
        "Opening Qty",
        "Purchased Qty",
        "Used Qty",
        "Closing Qty",
        "Opening A",
        "Purchased A",
        "Transferred A",
        "Closing A",
        "Opening B",
        "Transferred B",
        "Used B",
        "Closing B",
        "Remark",
      ]);

      filterData.forEach((item, index) => {
        sheetData.push([
          index + 1,
          item.stockNo || "",
          item.name || "",
          item.category || "",
          item.unit || "",
          item.openingQty ?? 0,
          item.purchasedQty ?? 0,
          item.usedQty ?? 0,
          item.closingQty ?? 0,
          item.openingQtyA ?? 0,
          item.purchasedQtyA ?? 0,
          item.transferredQtyA ?? 0,
          item.closingQtyA ?? 0,
          item.openingQtyB ?? 0,
          item.transferredQtyB ?? 0,
          item.usedQtyB ?? 0,
          item.closingQtyB ?? 0,
          item.remark || "",
        ]);
      });
    }

    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
    worksheet["!merges"] = merges;

    worksheet["!cols"] = isItemWise
      ? [
        { wch: 8 },
        { wch: 14 },
        { wch: 12 },
        { wch: 25 },
        { wch: 20 },
        { wch: 12 },
        { wch: 14 },
        { wch: 14 },
        { wch: 14 },
        { wch: 14 },
        { wch: 14 },
        { wch: 14 },
        { wch: 14 },
        { wch: 14 },
        { wch: 14 },
        { wch: 14 },
        { wch: 14 },
        { wch: 14 },
        { wch: 20 },
      ]
      : [
        { wch: 8 },
        { wch: 12 },
        { wch: 25 },
        { wch: 20 },
        { wch: 12 },
        { wch: 14 },
        { wch: 14 },
        { wch: 14 },
        { wch: 14 },
        { wch: 14 },
        { wch: 14 },
        { wch: 14 },
        { wch: 14 },
        { wch: 14 },
        { wch: 14 },
        { wch: 14 },
        { wch: 14 },
        { wch: 20 },
      ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Stock Report");

    XLSX.writeFile(
      workbook,
      `stock_report_${reportType}_${new Date().toISOString().split("T")[0]}.xlsx`,
    );
  };

  return (
    <div>
      <ToastContainer />

      <Link to="/logistics/stocklist">
        <button className="linkBtn mb-3">
          <i className="bi bi-arrow-left me-2"></i>Back to Stock List
        </button>
      </Link>

      <div className="mb-4 headStyle d-flex justify-content-between align-items-center">
        <h4 className="mb-0">Stock Report</h4>
      </div>

      <div
        className="mt-3"
        style={{
          padding: "16px",
          boxShadow: "0px 0px 10px 0px #e0e0e0",
          background: "white",
        }}
      >
        <label style={{ minWidth: "90px", fontWeight: 600, fontSize: "16px" }}>
          Select Report Type :
        </label>

        <div className="d-flex align-items-center gap-3 mt-3 flex-wrap">
          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              name="reportType"
              value="date"
              checked={reportType === "date"}
              onChange={(e) => {
                setReportType(e.target.value);
                setCurrentPage(0);
                setSearchText("");
              }}
            />
            <label className="form-check-label">Date Wise</label>
          </div>

          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              name="reportType"
              value="month"
              checked={reportType === "month"}
              onChange={(e) => {
                setReportType(e.target.value);
                setCurrentPage(0);
                setSearchText("");
              }}
            />
            <label className="form-check-label">Month Wise</label>
          </div>

          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              name="reportType"
              value="year"
              checked={reportType === "year"}
              onChange={(e) => {
                setReportType(e.target.value);
                setCurrentPage(0);
                setSearchText("");
              }}
            />
            <label className="form-check-label">Year Wise</label>
          </div>

          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              name="reportType"
              value="item"
              checked={reportType === "item"}
              onChange={(e) => {
                setReportType(e.target.value);
                setCurrentPage(0);
                setSearchText("");
              }}
            />
            <label className="form-check-label">Item Wise</label>
          </div>
        </div>

        {reportType === "date" ? (
          <div className="d-flex flex-wrap align-items-center gap-3 mt-3">
            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
              <label
                htmlFor="fromDate"
                style={{ width: "-webkit-fill-available" }}
              >
                From Date
              </label>
              <input
                className="form-control"
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setCurrentPage(0);
                }}
                style={{ border: "1px solid #1E2A5E", borderRadius: "8px" }}
              />
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
              <label
                htmlFor="toDate"
                style={{ width: "-webkit-fill-available" }}
              >
                To Date
              </label>
              <input
                className="form-control"
                type="date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setCurrentPage(0);
                }}
                style={{ border: "1px solid #1E2A5E", borderRadius: "8px" }}
              />
            </div>
          </div>
        ) : reportType === "month" ? (
          <div className="d-flex align-items-center gap-3 mt-3">
            <label htmlFor="month">Select Month</label>
            <input
              className="form-control"
              type="month"
              value={month}
              onChange={(e) => {
                setMonth(e.target.value);
                setCurrentPage(0);
              }}
              style={{ border: "1px solid #1E2A5E", borderRadius: "8px" }}
            />
          </div>
        ) : reportType === "year" ? (
          <div className="d-flex align-items-center gap-3 mt-3">
            <label htmlFor="year">Enter Year</label>
            <input
              className="form-control"
              type="number"
              min="1900"
              max="2100"
              value={year}
              onChange={(e) => {
                setYear(e.target.value);
                setCurrentPage(0);
              }}
              style={{ minWidth: "120px" }}
              placeholder="Enter Year"
            />
          </div>
        ) : (
          <div className="d-flex flex-wrap align-items-center gap-3 mt-3">
            <div className="d-flex align-items-center gap-2">
              <label style={{ minWidth: "70px" }}>Item</label>
              <select
                className="form-select"
                value={selectedStockId}
                onChange={(e) => {
                  setSelectedStockId(e.target.value);
                  setCurrentPage(0);
                }}
                style={{ minWidth: "280px" }}
              >
                <option value="">Select Item</option>
                {stockOptions.map((stock) => (
                  <option key={stock._id} value={stock._id}>
                    {stock.stockNo} - {stock.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
              <label>From Date</label>
              <input
                className="form-control"
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setCurrentPage(0);
                }}
                style={{ border: "1px solid #1E2A5E", borderRadius: "8px" }}
              />
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
              <label>To Date</label>
              <input
                className="form-control"
                type="date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setCurrentPage(0);
                }}
                style={{ border: "1px solid #1E2A5E", borderRadius: "8px" }}
              />
            </div>
          </div>
        )}
      </div>

      <div className="headStyle mt-4">
        <div className="d-flex mt-2 flex-wrap align-items-center gap-3 justify-content-between">
          <div className="d-flex flex-wrap align-items-center gap-3">
            <input
              className="form-control"
              type="search"
              placeholder="Search..."
              style={{
                maxWidth: "100%",
                border: "1px solid #1E2A5E",
                borderRadius: "8px",
              }}
              onChange={handleSearchChange}
              value={searchText}
            />

            <div className="d-flex align-items-center">
              <label className="me-2 pt-1">Count</label>
              <select
                className="form-select no-arrow"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(0);
                }}
                style={{ width: "auto" }}
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="15">15</option>
                <option value="20">20</option>
                <option value="25">25</option>
                <option value="30">30</option>
              </select>
            </div>
          </div>

          {["admin", "superadmin"].includes(userRole) && (
            <div>
              <button className="button-2 exxportBtn" onClick={handleExport}>
                Export to Excel
              </button>
            </div>
          )}
        </div>

        <div
          className="my-3 rounded table-responsive-sm"
          style={{ overflowX: "auto" }}
        >
          <table id="my-table" className="table table-bordered mb-0">
            <thead className="t_head">
              <tr className="text-center align-middle">
                <th rowSpan={2}>Sl No</th>
                {isItemWise && <th rowSpan={2}>Date</th>}
                <th rowSpan={2}>Stock No</th>
                <th rowSpan={2}>Product Name</th>
                <th rowSpan={2}>Category</th>
                <th rowSpan={2}>Unit</th>
                <th colSpan={4} style={{ background: "#e3f2fd", color: "#1565c0" }}>Total (BB + TY)</th>
                <th colSpan={4} style={{ background: "#e8f5e9", color: "#1b5e20" }}>Godown BB</th>
                <th colSpan={4} style={{ background: "#fff3e0", color: "#bf360c" }}>Godown TY</th>
                <th rowSpan={2}>Remark</th>
              </tr>
              <tr className="text-center align-middle">
                <th style={{ background: "#e3f2fd" }}>Opening</th>
                <th style={{ background: "#e3f2fd" }}>Purchased</th>
                <th style={{ background: "#e3f2fd" }}>Used</th>
                <th style={{ background: "#e3f2fd" }}>Closing</th>
                <th style={{ background: "#e8f5e9" }}>Opening</th>
                <th style={{ background: "#e8f5e9" }}>Purchased</th>
                <th style={{ background: "#e8f5e9" }}>Transferred</th>
                <th style={{ background: "#e8f5e9" }}>Closing</th>
                <th style={{ background: "#fff3e0" }}>Opening</th>
                <th style={{ background: "#fff3e0" }}>Transferred</th>
                <th style={{ background: "#fff3e0" }}>Used</th>
                <th style={{ background: "#fff3e0" }}>Closing</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={tableColSpan}>
                    <div className="d-flex justify-content-center m-3">
                      <div className="spinner"></div>
                    </div>
                  </td>
                </tr>
              ) : currentItems.length === 0 ? (
                <tr>
                  <td colSpan={tableColSpan}>
                    <div className="d-flex justify-content-center m-2">
                      <h6>Data Not Found</h6>
                    </div>
                  </td>
                </tr>
              ) : (
                currentItems.map((item, index) => (
                  <tr key={index + 1}>
                    <td className="text-center">
                      {currentPage * itemsPerPage + index + 1}
                    </td>

                    {isItemWise && <td>{item.date || "-"}</td>}

                    <td>{item.stockNo || "-"}</td>
                    <td>{item.name || "-"}</td>
                    <td>{item.category || "-"}</td>
                    <td>{item.unit || "-"}</td>

                    {/* Total columns */}
                    <td className="text-end" style={{ background: "#f3f9ff" }}>
                      {formatNumber(item.openingQty)}
                    </td>
                    <td
                      className="text-end"
                      style={{
                        background: "#f3f9ff",
                        ...(isItemWise && Number(item.purchasedQty) > 0
                          ? { color: "green", fontWeight: 600 }
                          : {}),
                      }}
                    >
                      {formatNumber(item.purchasedQty)}
                    </td>
                    <td
                      className="text-end"
                      style={{
                        background: "#f3f9ff",
                        ...(isItemWise && Number(item.usedQty) > 0
                          ? { color: "red", fontWeight: 600 }
                          : {}),
                      }}
                    >
                      {formatNumber(item.usedQty)}
                    </td>
                    <td className="text-end" style={{ background: "#f3f9ff", fontWeight: 600 }}>
                      {formatNumber(item.closingQty)}
                    </td>

                    {/* Godown A columns */}
                    <td className="text-end" style={{ background: "#f0fff4" }}>
                      {formatNumber(item.openingQtyA)}
                    </td>
                    <td
                      className="text-end"
                      style={{
                        background: "#f0fff4",
                        ...(isItemWise && Number(item.purchasedQtyA) > 0
                          ? { color: "#1b5e20", fontWeight: 600 }
                          : {}),
                      }}
                    >
                      {formatNumber(item.purchasedQtyA)}
                    </td><td
                      className="text-end"
                      style={{
                        background: "#f0fff4",
                        ...(isItemWise && Number(item.transferredQtyA) > 0
                          ? { color: "#1b5e20", fontWeight: 600 }
                          : {}),
                      }}
                    >
                      {formatNumber(item.transferredQtyA)}
                    </td>
                    <td className="text-end" style={{ background: "#f0fff4", fontWeight: 600 }}>
                      {formatNumber(item.closingQtyA)}
                    </td>

                    {/* Godown B columns */}
                    <td className="text-end" style={{ background: "#fff8f0" }}>
                      {formatNumber(item.openingQtyB)}
                    </td><td
                      className="text-end"
                      style={{
                        background: "#fff8f0",
                        ...(isItemWise && Number(item.transferredQtyB) > 0
                          ? { color: "#bf360c", fontWeight: 600 }
                          : {}),
                      }}
                    >
                      {formatNumber(item.transferredQtyB)}
                    </td>
                    <td
                      className="text-end"
                      style={{
                        background: "#fff8f0",
                        ...(isItemWise && Number(item.usedQtyB) > 0
                          ? { color: "#bf360c", fontWeight: 600 }
                          : {}),
                      }}
                    >
                      {formatNumber(item.usedQtyB)}
                    </td>
                    <td className="text-end" style={{ background: "#fff8f0", fontWeight: 600 }}>
                      {formatNumber(item.closingQtyB)}
                    </td>

                    <td>{item.remark || "-"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <ReactPaginate
          breakLabel="..."
          nextLabel="next >"
          onPageChange={handlePageClick}
          pageRangeDisplayed={5}
          pageCount={pageCount}
          previousLabel="< previous"
          renderOnZeroPageCount={null}
          containerClassName="pagination justify-content-center pagination-sm"
          pageClassName="page-item"
          pageLinkClassName="page-link"
          activeClassName="active"
          previousClassName="page-item"
          previousLinkClassName="page-link"
          nextClassName="page-item"
          nextLinkClassName="page-link"
          disabledClassName="disabled"
        />
      </div>
    </div>
  );
};

export default StockReport;
